import { trigger } from '@angular/animations';
import { CdkPortal, DomPortalOutlet } from '@angular/cdk/portal';
import {
  AfterContentInit,
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { clamp } from '../core/math/clamp';
import { PgFieldControl } from '../pg-field/pg-field-control';
import { PgOptionDirective } from './pg-option.directive';
import { getNonUniqueOptionValueError } from './pg-select-errors';
import {
  pgSelectTransitionBackdrop,
  pgSelectTransitionOptions,
} from './pg-select.animations';

@Component({
  selector: 'pg-select, grit-select',
  templateUrl: './pg-select.component.html',
  styleUrls: ['./pg-select.component.scss'],
  exportAs: 'pgSelect, gritSelect',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: PgFieldControl,
      useExisting: forwardRef(() => PgSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PgSelectComponent,
      multi: true,
    },
  ],
  animations: [
    trigger('transitionBackdrop', pgSelectTransitionBackdrop),
    trigger('transitionOptions', pgSelectTransitionOptions),
  ],
})
export class PgSelectComponent
  implements OnDestroy, ControlValueAccessor, AfterContentInit, AfterViewInit {
  public state: 'open' | 'closed' = 'closed';
  public transitionOptionsState = {
    value: 'closed',
    params: { top: '0px', left: '0px' },
  };

  @Input('id')
  @HostBinding('attr.id')
  id: string;

  @Input()
  placeholder: string;

  @ViewChild('label', { static: true })
  _labelChild: ElementRef;
  @ViewChild('backdrop')
  _backdropChild: ElementRef;
  @ViewChild('options')
  _optionsChild: ElementRef;

  @ViewChild('selectPortal', { static: true })
  selectPortal: CdkPortal;

  @ViewChild('backdropPortal', { static: true })
  backdropPortal: CdkPortal;

  selectPortalOutlet: DomPortalOutlet;
  backdropPortalOutlet: DomPortalOutlet;

  get labelEl(): HTMLDivElement {
    return this._labelChild.nativeElement;
  }

  get backdropEl(): HTMLDivElement {
    return this._backdropChild.nativeElement;
  }

  get optionsEl(): HTMLDivElement {
    return this._optionsChild.nativeElement;
  }

  private _subs: Subscription[] = [];

  @Input()
  public value;

  @Input()
  set display(display: string) {
    this._display = display;
  }
  get display() {
    return this._display;
  }
  protected _display = '';

  @Output()
  itemSelected = new EventEmitter<any>();

  @ContentChildren(PgOptionDirective)
  _optionChildren: QueryList<PgOptionDirective>;

  onChange = _ => {};
  onTouch = () => {};

  constructor(
    protected _elementRef: ElementRef,
    private _changeDetector: ChangeDetectorRef,
    _appRef: ApplicationRef,
    _cmptFactResolver: ComponentFactoryResolver,
    _injector: Injector
  ) {
    this.selectPortalOutlet = new DomPortalOutlet(
      document.body,
      _cmptFactResolver,
      _appRef,
      _injector
    );
    this.backdropPortalOutlet = new DomPortalOutlet(
      document.body,
      _cmptFactResolver,
      _appRef,
      _injector
    );
  }

  ngAfterViewInit() {
    this.backdropPortalOutlet.attachTemplatePortal(this.backdropPortal);
    this.selectPortalOutlet.attachTemplatePortal(this.selectPortal);
    setTimeout(() => {
      this._updateLocationParams();
    }, 100);
  }
  ngAfterContentInit() {
    // Check to make sure the options have unique values.
    this._optionChildren.reduce((acc, child, i) => {
      if (acc[child.value]) {
        throw getNonUniqueOptionValueError(child.value);
      }
      acc[child.value] = true;
      return acc;
    }, {});

    this._subs.push(
      merge(...this._optionChildren.map(x => x.itemSelected)).subscribe(
        (item: any) => {
          this.writeValue(item.value);
          this.display = item.display || item.value;
          this.hideOptions();
          this.itemSelected.emit(item);
        }
      )
    );

    const matchingChild = this._optionChildren.find(
      child => child.value === this.value
    );
    if (matchingChild) {
      this.display = matchingChild.display;
    }

    this._changeDetector.detectChanges();
  }

  ngOnDestroy() {
    this.backdropPortalOutlet.detach();
    this.selectPortalOutlet.detach();
    this._subs.forEach(s => !s.closed && s.unsubscribe());
  }

  getSelectDisplay() {
    const selectedChild = this._getSelectedOptionChild();

    if (selectedChild) {
      return selectedChild.display;
    }
    return this.display || this.value || this.placeholder || '';
  }

  showOptions() {
    const result = this._calculateOptionsPosition();
    this.optionsEl.scrollTop = result.scrollTop;

    this.transitionOptionsState = {
      value: 'open',
      params: {
        top: result.top + 'px',
        left: result.left + 'px',
      },
    };
    this.state = 'open';
  }

  hideOptions() {
    this.transitionOptionsState = {
      ...this.transitionOptionsState,
      value: 'closed',
    };
    this.state = 'closed';
    setTimeout(() => {
      this._updateLocationParams();
    }, 250);
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.onChange(obj);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    throw new Error('Method not implemented.');
  }

  private _getSelectedOptionChild() {
    const chosenChild = this._optionChildren.find(
      child => child.value === this.value
    );
    return chosenChild;
  }

  private _updateLocationParams() {
    const result = this._calculateOptionsPosition();
    this.transitionOptionsState = {
      ...this.transitionOptionsState,
      params: {
        top: result.top + 'px',
        left: result.left + 'px',
      },
    };
  }

  // Based on which item is selected we want the options pane to open
  // at a location where the selected option is right over the label.
  private _calculateOptionsPosition() {
    const optionsEl: HTMLElement = this.optionsEl;

    const selectedOptionCmpt = this._getSelectedOptionChild();
    let selectedOptionEl = null;
    if (selectedOptionCmpt && selectedOptionCmpt.elementRef) {
      selectedOptionEl = selectedOptionCmpt.elementRef.nativeElement;
    }

    const topPadding = 0;
    const leftPadding = 0;
    const originBoundingBox = this.labelEl.getBoundingClientRect();

    const result = {
      xDelta: -leftPadding || 0,
      yDelta: -topPadding || 0,
      scrollTop: 0,
    };

    if (selectedOptionEl) {
      const childHeight = selectedOptionEl.clientHeight;
      const childTargetScrollTop = Math.max(
        selectedOptionEl.offsetTop - childHeight,
        0
      );

      const maxScrollableHeight =
        optionsEl.scrollHeight - optionsEl.clientHeight;

      if (childTargetScrollTop > maxScrollableHeight) {
        result.yDelta =
          -topPadding -
          (childTargetScrollTop - maxScrollableHeight + childHeight);
      } else if (selectedOptionEl.offsetTop === 0) {
        result.yDelta = -topPadding;
      } else {
        result.yDelta = -topPadding - childHeight;
      }

      result.scrollTop = childTargetScrollTop;
    }

    const optionsTop = clamp(
      originBoundingBox.top + result.yDelta,
      0,
      window.innerHeight
    );
    const optionsLeft = clamp(
      originBoundingBox.left + result.xDelta,
      0,
      window.innerWidth
    );

    return {
      top: optionsTop,
      left: optionsLeft,
      scrollTop: result.scrollTop,
    };
  }
}
